import { useEffect, useState } from "react";

import { Button, Icon_Kebab, Menu, PageContainer, SpinnerCenter } from "@shapr3d/frontend-lib";
import { useLocation, useParams } from "react-router-dom";

import CTARow from "../components/CTARow/CTARow";
import { CTARowDivider } from "../components/CTARowDivider/CTARowDivider";
import Heading from "../components/Heading/Heading";
import SpaceCreationModal from "../components/SpaceCreationModal/SpaceCreationModal";
import SpaceNameChangeModal from "../components/SpaceNameChangeModal/SpaceNameChangeModal";
import TeamNameChangeModal from "../components/TeamNameChangeModal/TeamNameChangeModal";
import UnderMaintenanceOrChildren from "../components/UnderMaintenanceOrChildren/UnderMaintenanceOrChildren";
import { defaultTeamspaceName } from "../config";
import useAccount from "../hooks/useAccount";
import { useTeam } from "../hooks/useTeams";
import { type TeamSpace, useTeamSpaces } from "../hooks/useTeamSpaces";
import { useTracker } from "../hooks/useTracker";
import { ElementClickAction, ElementClickLocation } from "../lib/TrackingAdapter";
import { type URLState, URLStateActions } from "../types";

import style from "./TeamSettingsPage.module.css";

const TeamSettingsPage = () => {
  const tracker = useTracker();
  const { teamID } = useParams();
  const { data: accountData, isLoading: isAccountLoading } = useAccount();

  const [isTeamNameChangeModalOpen, setIsNameChangeModalOpen] = useState(false);
  const [isSpaceNameChangeModalOpen, setIsSpaceNameChangeModalOpen] = useState(false);
  const [isSpaceCreationModalOpen, setIsSpaceCreationModalOpen] = useState(false);
  const [selectedSpace, setSelectedSpace] = useState<TeamSpace | null>(null);
  const [historyState, setHistoryState] = useState<URLState | null>(null);

  const { teamData, isOwner, isSeatManager, isLoading: isTeamLoading } = useTeam(teamID);
  const canManageSpaces = isSeatManager && teamData?.addons?.teamspaceManagement;

  const { data: teamSpaceData, isLoading: isSpacesLoading } = useTeamSpaces(teamID);

  const isLoading = isAccountLoading || isSpacesLoading || isTeamLoading;

  const { state } = useLocation();
  useEffect(() => {
    if (state) {
      setHistoryState(state);
    }
  }, [state]);

  useEffect(() => {
    if (isLoading || !historyState) {
      return;
    }

    if (
      historyState.Action === URLStateActions.OpenRenameSpaceModal &&
      historyState.Params.SpaceID
    ) {
      const space = teamSpaceData?.spaces.find((space) => space.id === historyState.Params.SpaceID);
      if (space) {
        setSelectedSpace(space);
        setIsSpaceNameChangeModalOpen(true);
        setHistoryState(null);
      }
    }
  }, [teamID, teamSpaceData, isLoading, historyState]);

  if (isLoading || !accountData) {
    return (
      <PageContainer>
        <SpinnerCenter />
      </PageContainer>
    );
  }

  return (
    <UnderMaintenanceOrChildren>
      <PageContainer>
        <div className={style.flexContainer}>
          <div className={style.titleContainer}>
            <Heading type="h2">Team settings</Heading>
            {canManageSpaces && (
              <Button
                variant="primary"
                onClick={() => {
                  setIsSpaceCreationModalOpen(true);
                  tracker.trackButtonClick({
                    action: ElementClickAction.CREATE_SPACE,
                  });
                }}
              >
                Create space
              </Button>
            )}
          </div>
          <CTARow
            title="Team name"
            text={<div className={style.userInputText}>{teamData?.name}</div>}
            CTA={
              <Button
                variant="borderless"
                disabled={!isOwner}
                onClick={() => {
                  setIsNameChangeModalOpen(true);
                  tracker.trackButtonClick({
                    action: ElementClickAction.UPDATE_TEAM_NAME,
                  });
                }}
              >
                Edit
              </Button>
            }
          />
          <span className={style.tableHeader}>Spaces</span>
          {teamSpaceData?.spaces
            .sort((a, b) => a.name?.localeCompare(b.name || "") || 0)
            .map((space) => (
              <div key={space.id}>
                <CTARowDivider />
                <CTARow
                  title={space.name || defaultTeamspaceName}
                  CTA={
                    <Menu
                      activator={
                        <Button variant="borderless">
                          <Icon_Kebab />
                        </Button>
                      }
                      className={style.contextMenu}
                      onOpen={() => {
                        setSelectedSpace(space);
                        tracker.trackButtonClick({
                          action: ElementClickAction.OPEN_SPACE_OPTIONS_MENU,
                        });
                      }}
                    >
                      <Menu.Item
                        closeOnClick
                        onClick={() => {
                          setIsSpaceNameChangeModalOpen(true);
                          tracker.trackButtonClick({
                            action: ElementClickAction.RENAME_SPACE,
                            location: ElementClickLocation.SPACE_OPTIONS_MENU,
                          });
                        }}
                      >
                        <span>Rename space</span>
                      </Menu.Item>
                    </Menu>
                  }
                />
              </div>
            ))}
          <span className={style.bottomSpacing} />
        </div>
      </PageContainer>
      <TeamNameChangeModal
        onClose={() => setIsNameChangeModalOpen(false)}
        name={teamData?.name}
        open={isTeamNameChangeModalOpen}
      />
      <SpaceNameChangeModal
        onClose={() => setIsSpaceNameChangeModalOpen(false)}
        name={selectedSpace?.name}
        spaceID={selectedSpace?.id ?? ""}
        open={isSpaceNameChangeModalOpen && Boolean(selectedSpace?.id)}
      />
      <SpaceCreationModal
        onClose={() => setIsSpaceCreationModalOpen(false)}
        open={isSpaceCreationModalOpen}
      />
    </UnderMaintenanceOrChildren>
  );
};

export default TeamSettingsPage;
